<script setup>
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  inventaire:           { type: Object,  default: () => ({}) },
  show_type_inventaire: { type: Boolean, default: false },
  show_type_taux:       { type: Boolean, default: false },
  can_update:           { type: Boolean, default: false },
  can_see_costs:        { type: Boolean, default: false },
  inventaires_url:      { type: String,  default: '/inventaires' },
})

const destroyLabel = computed(() => {
  if (!props.inventaire.actif) return 'Activer'
  return props.inventaire.dependant ? 'Désactiver' : 'Effacer'
})

const destroyIcon = computed(() => {
  if (!props.inventaire.actif) return 'toggle_on'
  return props.inventaire.dependant ? 'toggle_off' : 'delete'
})

const destroyVariant = computed(() => {
  if (!props.inventaire.actif) return 'text-tertiary border-tertiary/30 hover:bg-tertiary/10'
  return props.inventaire.dependant
    ? 'text-yellow-500 border-yellow-500/30 hover:bg-yellow-500/10'
    : 'text-error border-error/30 hover:bg-error/10'
})

function confirmDestroy() {
  const nom = props.inventaire.nom || props.inventaire.code
  const msg = props.inventaire.actif
    ? (props.inventaire.dependant ? `Désactiver ${nom} ?` : `Effacer ${nom} ?`)
    : `Réactiver ${nom} ?`
  if (!confirm(msg)) return
  router.delete(`/inventaires/${props.inventaire.id}`, {
    onSuccess: () => window.location.href = props.inventaires_url,
  })
}

function formatTaux(val, unit) {
  if (val == null || val === '') return '—'
  const num = Number(val).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return unit ? `${num} $ / ${unit}` : `${num} $`
}

function formatQty(val) {
  if (val == null || val === '') return '—'
  return Number(val).toLocaleString('fr-CA')
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Inventaires &rsaquo; {{ inventaire.nom }}
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              {{ inventaire.nom }}
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ inventaire.code }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <button
              v-if="can_update"
              @click="confirmDestroy"
              :class="['flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest rounded-md border bg-transparent transition-all', destroyVariant]"
            >
              <span class="material-symbols-outlined text-base">{{ destroyIcon }}</span>
              {{ destroyLabel }}
            </button>
            <a
              v-if="can_update"
              :href="inventaire.edit_url"
              class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-base">edit</span>
              Modifier
            </a>
            <a
              :href="inventaires_url"
              class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
            >
              <span class="material-symbols-outlined text-base">arrow_back</span>
              Retour
            </a>
          </div>
        </div>

        <!-- Detail Card -->
        <div class="bg-surface-container rounded-xl border-l-4 border-primary shadow-2xl shadow-black/40 overflow-hidden">
          <div class="divide-y divide-outline-variant/10">

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Code</span>
              <span class="font-mono text-sm font-bold text-primary">{{ inventaire.code }}</span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Nom</span>
              <span class="text-sm text-on-surface font-semibold">{{ inventaire.nom || '—' }}</span>
            </div>

            <div v-if="show_type_inventaire && inventaire.type_inventaire" class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Type</span>
              <span class="text-sm text-on-surface font-semibold">
                {{ inventaire.type_inventaire.code }}
                <span v-if="inventaire.type_inventaire.description" class="text-outline font-normal ml-1">— {{ inventaire.type_inventaire.description }}</span>
              </span>
            </div>

            <div v-if="can_see_costs" class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Taux</span>
              <span class="text-sm text-on-surface font-bold font-mono">
                {{ formatTaux(inventaire.taux, inventaire.type_taux ? inventaire.type_taux.periode || inventaire.type_taux.code : null) }}
              </span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Quantité</span>
              <span class="text-sm text-on-surface font-mono">{{ formatQty(inventaire.quantite) }}</span>
            </div>

            <div v-if="inventaire.description" class="flex items-start px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0 pt-0.5">Description</span>
              <span class="text-sm text-on-surface/80 whitespace-pre-line">{{ inventaire.description }}</span>
            </div>

            <div class="flex items-center px-8 py-4 gap-6">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline w-40 shrink-0">Statut</span>
              <span
                :class="inventaire.actif
                  ? 'bg-tertiary/15 text-tertiary border border-tertiary/30'
                  : 'bg-outline/10 text-outline border border-outline/20'"
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="inventaire.actif ? 'bg-tertiary' : 'bg-outline/50'"></span>
                {{ inventaire.actif ? 'Actif' : 'Inactif' }}
              </span>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
